import { useMemo } from "react";
import type { AdvertisementDto } from "@/api/advertising";
import type { BuyerLeadSource } from "@/api/buyers";
import { FormField } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { sourceForChannel } from "@/lib/advertisingChannelSources";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useBuyerTeams } from "@/hooks/buyers/useBuyerTeams";
import { useSpecialistCandidates } from "@/hooks/buyers/useBuyerAssignments";
import { useAttributableAdvertisements } from "@/hooks/buyers/useAdvertising";
import { smartImportCopy } from "./smartImportCopy";
import type { SmartImportPlacement } from "./smartImportReview";

/**
 * Where these buyers land: team, owner, advertisement (D10, D16).
 *
 * <p>🔴 <b>Leaving all three blank is a CHOICE, and the page says which one.</b>
 * D10's whole point is that "today's routing" is an outcome an operator should
 * be able to read off the screen rather than infer from an absence — a blank
 * team does not mean no team, it means the team each row's advertisement or
 * site implies.</p>
 *
 * <p>The advertisement block is permission-gated the way
 * `AdvertisementPicker` is: `attributable()` 403s for anybody without
 * `BuyerAdvertising.Read`, so an F2 is offered no control rather than a control
 * that cannot load.</p>
 */

export const ADVERTISING_READ_PERMISSION = "BuyerAdvertising.Read";

export interface SmartImportPickersProps {
  placement: SmartImportPlacement;
  onChange: (placement: SmartImportPlacement) => void;
  /**
   * The Source every tab should take when an advertisement is picked (D16),
   * or null when the pick was cleared. The caller decides what to do with it,
   * because a tab's Source lives in the review state and this component does
   * not own that.
   */
  onAdvertisementSource?: (source: BuyerLeadSource | null) => void;
  disabled?: boolean;
}

export function SmartImportPickers({
  placement,
  onChange,
  onAdvertisementSource,
  disabled,
}: SmartImportPickersProps) {
  const auth = useOptionalAuth();
  const canReadAdvertising = auth?.hasPermission(ADVERTISING_READ_PERMISSION) ?? false;

  const teams = useBuyerTeams();
  const candidates = useSpecialistCandidates(null);
  const ads = useAttributableAdvertisements({ enabled: canReadAdvertising });

  const teamRows = useMemo(
    () => (teams.data ?? []).filter((t) => t.status !== "Archived"),
    [teams.data],
  );

  /**
   * 🔴 The candidate list is the SERVER's answer to "who may be handed a buyer
   * about to be created", and the team choice narrows it rather than replacing
   * it. Reading the team's own roster instead would offer people the assignment
   * path refuses — it validates the F1/F2 role and the caller's dispatch scope —
   * so the operator would pick a name and be refused at commit.
   */
  const assignable = useMemo(() => {
    const all = candidates.data?.candidates ?? [];
    return placement.buyerTeamId
      ? all.filter((c) => c.buyerTeamId === placement.buyerTeamId)
      : all;
  }, [candidates.data, placement.buyerTeamId]);

  const adRows = useMemo(() => {
    const all = ads.data ?? [];
    return placement.buyerTeamId
      ? all.filter((a) => a.buyerTeamId === placement.buyerTeamId)
      : all;
  }, [ads.data, placement.buyerTeamId]);

  /**
   * Changing the team drops anything the new team does not offer.
   *
   * <p>A hidden value still rides the commit, so an owner or an advertisement
   * left over from the previous team would attribute the whole import to a team
   * the operator has just moved away from — invisibly, because the control no
   * longer lists it.</p>
   */
  const pickTeam = (teamId: string | null): void => {
    const keepsOwner =
      placement.assignToSpecialistUserId != null &&
      (candidates.data?.candidates ?? []).some(
        (c) =>
          c.specialistUserId === placement.assignToSpecialistUserId &&
          (teamId == null || c.buyerTeamId === teamId),
      );
    const keptAd =
      placement.advertisementId != null
        ? (ads.data ?? []).find((a) => a.id === placement.advertisementId)
        : undefined;
    const keepsAd = keptAd != null && (teamId == null || keptAd.buyerTeamId === teamId);

    onChange({
      buyerTeamId: teamId,
      assignToSpecialistUserId: keepsOwner ? placement.assignToSpecialistUserId : null,
      advertisementId: keepsAd ? placement.advertisementId : null,
    });
    if (!keepsAd && placement.advertisementId != null) onAdvertisementSource?.(null);
  };

  const pickAdvertisement = (ad: AdvertisementDto | null): void => {
    onChange({ ...placement, advertisementId: ad?.id ?? null });
    onAdvertisementSource?.(ad ? sourceForChannel(ad.channel) : null);
  };

  return (
    <section
      aria-label="Where these buyers land"
      className="grid gap-space-3"
      data-testid="smart-import-pickers"
    >
      <p className="text-small text-gray-mid" data-testid="smart-import-routing-note">
        {smartImportCopy.routingNote}
      </p>

      <FormField label={smartImportCopy.teamLabel}>
        <Select
          data-testid="smart-import-team"
          disabled={disabled || teams.isPending}
          value={placement.buyerTeamId ?? ""}
          onChange={(e) => pickTeam(e.target.value || null)}
        >
          <option value="">{smartImportCopy.teamNone}</option>
          {teamRows.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label={smartImportCopy.assignToLabel}
        helperText={
          placement.buyerTeamId
            ? smartImportCopy.assignToNeedsTeamHint
            : smartImportCopy.assignToAllHint
        }
      >
        <Select
          data-testid="smart-import-assign-to"
          disabled={disabled || candidates.isPending}
          value={placement.assignToSpecialistUserId ?? ""}
          onChange={(e) =>
            onChange({ ...placement, assignToSpecialistUserId: e.target.value || null })
          }
        >
          <option value="">{smartImportCopy.assignToNone}</option>
          {assignable.map((c) => (
            <option key={c.specialistUserId} value={c.specialistUserId}>
              {c.displayName ?? c.specialistUserId}
            </option>
          ))}
        </Select>
      </FormField>

      {canReadAdvertising && (
        <FormField
          label={smartImportCopy.advertisementLabel}
          /*
            BOTH facts, always. D16 says two separate things about this control
            — the pick refills every tab's Source, and the list is narrowed to
            the team — and showing only one of them depending on whether a team
            is chosen would leave an operator with a team selected unaware that
            their Sources are about to move.
          */
          helperText={
            placement.buyerTeamId
              ? `${smartImportCopy.advertisementTeamHint} ${smartImportCopy.advertisementSourceNote}`
              : smartImportCopy.advertisementSourceNote
          }
        >
          <Select
            data-testid="smart-import-advertisement"
            disabled={disabled || ads.isPending}
            value={placement.advertisementId ?? ""}
            onChange={(e) => {
              const next = e.target.value;
              pickAdvertisement(next ? (adRows.find((a) => a.id === next) ?? null) : null);
            }}
          >
            <option value="">{smartImportCopy.advertisementNone}</option>
            {adRows.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.channel}
              </option>
            ))}
          </Select>
        </FormField>
      )}
    </section>
  );
}
